import assert from "minimalistic-assert";
import * as z from "zod/mini";

import * as channel from "./channel.ts";
import {message_id_schema} from "./message_id.ts";
import * as stream_topic_history from "./stream_topic_history.ts";
import {appearance_schema, display_appearance_schema} from "./topic_appearance.ts";
import * as util from "./util.ts";

const stream_topic_history_response_schema = z.object({
    topics: z.array(
        z.object({
            name: z.string(),
            max_id: message_id_schema,
            timestamp: z.number(),
            icon: z.optional(appearance_schema.shape.icon),
            icon_color: z.optional(appearance_schema.shape.icon_color),
            icon_url: display_appearance_schema.shape.icon_url,
            icon_color_light: appearance_schema.shape.icon_color_light,
            icon_color_dark: appearance_schema.shape.icon_color_dark,
            text_color_light: appearance_schema.shape.text_color_light,
            text_color_dark: appearance_schema.shape.text_color_dark,
            emoji: appearance_schema.shape.emoji,
            has_description: display_appearance_schema.shape.has_description,
            is_restricted: z.optional(z.boolean()),
        }),
    ),
});

const pending_on_success_callbacks = new Map<string, (() => void)[]>();

export const MAX_RETRIES = 5;

function fetch_channel_history_with_retry(
    stream_id: number,
    include_all_topics: boolean,
    attempt = 1,
): void {
    const request_key = `${stream_id}:${include_all_topics ? "all" : "restricted"}`;
    if (attempt > MAX_RETRIES) {
        pending_on_success_callbacks.delete(request_key);
        if (!include_all_topics) {
            stream_topic_history.remove_request_pending_for(stream_id);
        }
        return;
    }

    const url = "/json/users/me/" + stream_id + "/topics";
    void channel.get({
        url,
        data: {allow_empty_topic_name: true, include_all_topics},
        success(raw_data) {
            const data = stream_topic_history_response_schema.parse(raw_data);
            const server_history = data.topics;
            stream_topic_history.add_history(stream_id, server_history);
            if (!include_all_topics) {
                stream_topic_history.remove_request_pending_for(stream_id);
            }
            const callbacks = pending_on_success_callbacks.get(request_key) ?? [];
            for (const callback of callbacks) {
                callback();
            }
            pending_on_success_callbacks.delete(request_key);
        },
        error(xhr) {
            const retry_delay_secs = util.get_retry_backoff_seconds(xhr, attempt);
            setTimeout(() => {
                fetch_channel_history_with_retry(stream_id, include_all_topics, attempt + 1);
            }, retry_delay_secs * 1000);
        },
    });
}

export function get_server_history(
    stream_id: number,
    on_success: () => void,
    include_all_topics = false,
): void {
    const request_key = `${stream_id}:${include_all_topics ? "all" : "restricted"}`;
    if (!include_all_topics && stream_topic_history.has_history_for(stream_id)) {
        on_success();
        return;
    }
    if (pending_on_success_callbacks.has(request_key)) {
        const callbacks = pending_on_success_callbacks.get(request_key) ?? [];
        callbacks.push(on_success);
        pending_on_success_callbacks.set(request_key, callbacks);
        return;
    }

    if (!include_all_topics && stream_topic_history.is_request_pending_for(stream_id)) {
        const callbacks = pending_on_success_callbacks.get(request_key) ?? [];
        callbacks.push(on_success);
        pending_on_success_callbacks.set(request_key, callbacks);
        return;
    }

    if (!include_all_topics) {
        stream_topic_history.add_request_pending_for(stream_id);
    }
    pending_on_success_callbacks.set(request_key, [on_success]);

    fetch_channel_history_with_retry(stream_id, include_all_topics);
}

export function update_topic_last_message_id(
    stream_id: number,
    topic_name: string,
    update_dom_on_success: () => void,
): void {
    void channel.get({
        url: "/json/messages",
        data: {
            narrow: JSON.stringify([
                {operator: "stream", operand: stream_id},
                {operator: "topic", operand: topic_name},
            ]),
            anchor: "newest",
            num_before: 1,
            num_after: 0,
            allow_empty_topic_name: true,
        },
        success(data) {
            const {messages} = z
                .object({
                    messages: z.array(
                        z.object({
                            id: message_id_schema,
                            timestamp: z.number(),
                        }),
                    ),
                })
                .parse(data);
            if (messages.length !== 1) {
                return;
            }

            const last_message = messages[0];
            assert(last_message !== undefined);
            stream_topic_history.add_history(stream_id, [
                {
                    name: topic_name,
                    max_id: last_message.id,
                    timestamp: last_message.timestamp,
                },
            ]);
            update_dom_on_success();
        },
        error() {
            // Ideally we would retry since we should always be able to get a success response
            // from the server for this request, but for now we just ignore the error.
        },
    });
}
